import { ApiError } from '@gorila-shared-ui/components';
import { useState } from 'react';
import { RecoilState, SetterOrUpdater, useRecoilState } from 'recoil';
import { useEffectOnce, useUpdateEffect } from '../../hooks/useCustomEffects';
import { PaginatedResponse } from '../../types/app';

export function PaginatedProvider<T>({
  searchState,
  pageState,
  getPaginated,
  setPaginated,
  onSearchChange,
  onUpdateFinish,
  setLoading,
  update,
}: {
  searchState: RecoilState<string | undefined>;
  pageState: RecoilState<number>;
  setLoading: SetterOrUpdater<boolean>;
  getPaginated: (
    page: number,
    search?: string
  ) => Promise<{ paginatedResponse?: PaginatedResponse<T>; error?: ApiError }>;
  setPaginated: SetterOrUpdater<PaginatedResponse<T> | undefined>;
  onUpdateFinish: () => void;
  onSearchChange: () => void;
  update?: string;
}) {
  const [search, setSearch] = useRecoilState(searchState);
  const [page, setPage] = useRecoilState(pageState);
  const [initialLaod, setInitialLoad] = useState(true);

  const load = async () => {
    setLoading(true);
    const { paginatedResponse, error } = await getPaginated(page, search);
    if (!error && paginatedResponse) {
      if (page > 1) {
        setPaginated((prev) => ({
          summary: paginatedResponse.summary,
          hasNext: paginatedResponse.hasNext,
          total: paginatedResponse.total,
          items: [...prev!.items, ...paginatedResponse.items],
        }));
      } else {
        if (initialLaod && paginatedResponse.items.length === 0) {
          setPaginated(undefined);
        } else {
          setPaginated(paginatedResponse);
        }
      }
    } else {
      setPaginated(undefined);
    }
    if (initialLaod) setInitialLoad(false);
    setLoading(false);
  };

  useEffectOnce(() => {
    load();
    return () => {
      setSearch('');
      setPage(1);
    };
  });

  useUpdateEffect(() => {
    setPage(0);
    onSearchChange();
  }, [search]);

  useUpdateEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }

    load();
  }, [page]);

  useUpdateEffect(() => {
    if (!update) return;

    load().finally(() => {
      onUpdateFinish();
    });
  }, [update]);

  return null;
}
